import {
  CreateExportDTO,
  CreateExportResponse,
  ExportBackendFormat,
  ExportBackendStatus,
  ExportDownloadResponse,
  ExportEmployee,
  ExportFormat,
  ExportStatus,
  PaginatedResponse,
  ProcessedPayrollCard,
  ProcessTypeOption,
  RecentExport,
  RecentExportResponse,
  SendShareEmailsDTO,
  SendShareEmailsResponse,
  ShareNotification,
  ShareNotificationFilters,
  ShareNotificationResponse,
  ShareNotificationsPage,
  ShareNotificationsResponse
} from './types'
import {
  buildDate,
  buildDateRange,
  buildTitle,
  formatExportDate,
  isoDate,
  isPayrollProcess,
  relativeTime
} from './display-name.helper'
import { getData, TableDataResponse } from '../get-table'
import { HttpService } from '../http.service'
import { API_PAYROLL, ENV } from '../../env'
export * from './types'

/* API GW del nuevo modulo de payroll (servicio de documentos/recibos). */
const httpPayroll = new HttpService(API_PAYROLL[ENV()]())

/**
 * Capa de servicio para "Exportar Recibos de pago".
 *
 * - LECTURA de nominas procesadas y tipos de proceso: servicio get-table.
 * - LECTURA de recibos (getEmployees), CREACION de export (createExport) y
 *   LISTADO de exportaciones recientes (getRecentExports): nuevo modulo de
 *   payroll (documents/*), via httpPayroll.
 * - El formateo de fechas y titulos (displayName) vive en display-name.helper.
 */

/* -------------------------------------------------------------------------- */
/* Tipos de proceso (para el filtro)                                           */
/* -------------------------------------------------------------------------- */

/* Fila cruda devuelta por get-table para pg_processes */
interface ProcessRow {
  id: string
  code: string
  name: string
}

/**
 * Procesos habilitados para el pais de la nomina seleccionada.
 * El tag :countryId se reemplaza en get-table con el pais del payroll store.
 */
export async function getProcessTypes(): Promise<ProcessTypeOption[]> {
  const result = await getData<[TableDataResponse<ProcessRow>]>({
    tables: ['pg_processes'],
    fields: ['processes.id', 'processes.code', 'processes.name'],
    where: ['processes.countries ? :countryId'],
    modifier: ['ORDER BY processes.code'],
    options: {
      outputFormat: 'json'
    }
  })

  return result[0].results.map(process => ({
    id: process.id,
    name: process.name
  }))
}

/* -------------------------------------------------------------------------- */
/* Nominas procesadas                                                          */
/* -------------------------------------------------------------------------- */

/* Fila cruda devuelta por get-table para pg_closed_payrolls */
interface ClosedPayrollRow {
  id: string
  processed_payroll_id: string
  closed_date: string
  start_date: string
  end_date: string
  employees: number
  process_id: string
  code: string
  name: string
  /* Codigo de la frecuencia del payroll (S/BS/M/Q) */
  frequency: string
}

/**
 * Nominas cerradas del payroll seleccionado, listas para exportar recibos.
 * El tag :payrollId se reemplaza en get-table con el payroll del store.
 *
 * NOTA: por ahora se filtran las NO pagadas (is_paid = false) para la prueba.
 * El comportamiento definitivo es traer las pagadas (is_paid = true).
 */
export async function getProcessedPayrolls(): Promise<ProcessedPayrollCard[]> {
  const result = await getData<[TableDataResponse<ClosedPayrollRow>]>({
    tables: ['pg_closed_payrolls'],
    fields: [
      'closed_payrolls.id',
      'closed_payrolls.processed_payroll_id',
      'closed_payrolls.closed_date',
      'processed_payrolls.start_date',
      'processed_payrolls.end_date',
      'jsonb_array_length(processed_payrolls.employees::jsonb) as employees',
      'processed_payrolls.process_id',
      'processes.code',
      'processes.name',
      'frequencies.code as frequency'
    ],
    join: [
      'INNER JOIN processed_payrolls ON processed_payrolls.id = closed_payrolls.processed_payroll_id',
      'INNER JOIN processes ON processes.id = processed_payrolls.process_id',
      'INNER JOIN payrolls ON payrolls.id = closed_payrolls.payroll_id',
      'INNER JOIN frequencies ON frequencies.id = payrolls.frequency_id'
    ],
    where: [
      'closed_payrolls.payroll_id = :payrollId',
      'closed_payrolls.is_paid = true'
    ],
    modifier: ['ORDER BY closed_payrolls.closed_date DESC'],
    options: {
      outputFormat: 'json'
    }
  })

  return result[0].results.map((row, index) => ({
    id: row.id,
    processedPayrollId: row.processed_payroll_id,
    name: buildTitle(row),
    period: buildDateRange(row.start_date, row.end_date),
    endDate: isoDate(row.end_date),
    employeesCount: Number(row.employees) || 0,
    date: buildDate(row.closed_date),
    /* La mas reciente (primera por closed_date DESC) */
    isMostRecent: index === 0,
    processCode: row.process_id,
    processLabel: row.name,
    isPayroll: isPayrollProcess(row.code)
  }))
}

/* -------------------------------------------------------------------------- */
/* Empleados / Recibos de pago                                                 */
/* -------------------------------------------------------------------------- */

/* Recibo de pago (payment slip) devuelto por el servicio documents */
interface PaymentSlipResponse {
  /* Id del recibo de pago (payment slip) */
  id: string
  closedPayrollId: string
  employeeId: string
  employeeCode: string
  employeeFullname: string
  createdAt: string
}

/**
 * Recibos de pago (payment slips) generados para una nomina cerrada.
 * Consulta el servicio documents: GET /documents/payment-slips.
 *
 * Cada item representa un recibo de un empleado. El `id` que se retorna es el
 * id del recibo (payment slip), que es lo que luego se envia al exportar.
 */
export async function getEmployees(
  closedPayrollId: string
): Promise<ExportEmployee[]> {
  if (!closedPayrollId) return []

  const response = await httpPayroll.get<PaymentSlipResponse[]>(
    'documents/payment-slips',
    {
      params: { closedPayrollId }
    }
  )

  const slips = response?.data ?? []

  return slips
    .map(slip => ({
      id: slip.id,
      employeeId: slip.employeeId,
      code: slip.employeeCode,
      name: (slip.employeeFullname ?? '').replace(/\s+/g, ' ').trim(),
      included: true
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/* -------------------------------------------------------------------------- */
/* Exportar                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Crea una solicitud de exportacion de recibos de pago.
 * POST documents/payment-slips/exports (nuevo modulo de payroll).
 */
export async function createExport(
  dto: CreateExportDTO
): Promise<CreateExportResponse> {
  const response = await httpPayroll.post<
    CreateExportDTO,
    CreateExportResponse
  >('documents/payment-slips/exports', dto)

  return response?.data ?? { id: `exp-${Date.now()}` }
}

/* -------------------------------------------------------------------------- */
/* Exportaciones recientes                                                     */
/* -------------------------------------------------------------------------- */

/* Mapea el estado del backend al estado que entiende la UI */
const mapExportStatus = (status: ExportBackendStatus): ExportStatus => {
  switch (status) {
    case 'COMPLETED':
      return 'COMPLETED'
    case 'FAILED':
      return 'ERROR'
    /* REQUESTED y PROCESSING se muestran como en proceso */
    case 'REQUESTED':
    case 'PROCESSING':
    default:
      return 'PROCESSING'
  }
}

/* Mapea el formato del backend al formato que muestra la UI */
const mapExportFormat = (
  format: RecentExportResponse['format']
): ExportFormat => (format === 'ZIP' ? 'ZIP' : 'PDF')

/* Cuantas exportaciones recientes se piden (coincide con las que muestra la tabla) */
const RECENT_EXPORTS_PAGE_SIZE = 10

/**
 * Exportaciones recientes solicitadas por el usuario actual.
 * GET documents/payment-slips/exports?page=&pageSize= (modulo de payroll).
 *
 * El endpoint devuelve una pagina (`items` + totales), no un arreglo plano, y
 * resuelve el solicitante desde el token: no acepta `requestedBy` por query.
 *
 * NOTA: el backend no expone progreso granular ni URL de descarga en el
 * listado; la descarga se resuelve en un endpoint aparte (ver download()).
 */
export async function getRecentExports(): Promise<RecentExport[]> {
  const response = await httpPayroll.get<
    PaginatedResponse<RecentExportResponse>
  >('documents/payment-slips/exports', {
    params: { page: 1, pageSize: RECENT_EXPORTS_PAGE_SIZE }
  })

  const exports = response?.data?.items ?? []

  return exports.map(item => {
    const uiStatus = mapExportStatus(item.status)
    /* Para exportaciones completadas mostramos cuando quedo lista (generatedAt);
     * para el resto, cuando se solicito (requestedAt). */
    const relativeBase =
      uiStatus === 'COMPLETED' ? item.generatedAt : item.requestedAt

    return {
      id: item.id,
      createdAt: formatExportDate(item.requestedAt),
      format: mapExportFormat(item.format),
      payrollName: item.displayName,
      employeesCount: Number(item.paymentSlipsQuantity) || 0,
      status: uiStatus,
      statusLabel:
        uiStatus === 'ERROR' && item.failureReason
          ? item.failureReason
          : relativeTime(relativeBase),
      /* El backend expone el tamano del artefacto en bytes;
       * el formateo a KB/MB se resuelve en la vista. */
      sizeBytes: item.artifactSize
    }
  })
}

/**
 * Obtiene la URL prefirmada de S3 para descargar una exportacion completada.
 * GET documents/payment-slips/exports/{id}/download (modulo de payroll).
 */
export async function getExportDownloadUrl(exportId: string): Promise<string> {
  const response = await httpPayroll.get<ExportDownloadResponse>(
    `documents/payment-slips/exports/${exportId}/download`
  )

  return response?.data?.downloadUrl ?? ''
}

/* -------------------------------------------------------------------------- */
/* Notificaciones (proveedor document-sharing)                                 */
/* -------------------------------------------------------------------------- */

/* Ruta del servicio de shares en el API GW del modulo de payroll */
const SHARES_PATH = 'documents/document-shares'

/**
 * Encola el envio de correos con el recibo y su enlace magico.
 * POST documents/document-shares (modulo de payroll, contexto document-sharing).
 *
 * El backend resuelve los recibos a partir de los mismos filtros que usa la
 * pantalla (nomina cerrada + empleados), por lo que no se envian ids de recibo.
 *
 * El share se crea sin vigencia (`expiresInDays: null`): el enlace del correo no
 * caduca, lo que expira es el token de acceso de un solo uso que se pide desde el.
 */
export async function sendShareEmails(
  dto: SendShareEmailsDTO
): Promise<SendShareEmailsResponse> {
  const response = await httpPayroll.post<
    SendShareEmailsDTO,
    SendShareEmailsResponse
  >(SHARES_PATH, {
    closedPayrollIds: dto.closedPayrollIds,
    employeeIds: dto.employeeIds,
    justification: dto.justification,
    expiresInDays: dto.expiresInDays ?? null
  })

  return response?.data ?? { total: 0, requested: 0, shareIds: [] }
}

/* Los filtros viajan como CSV en la query string (asi los parsea el backend) */
const toCsv = (values?: string[]): string | undefined =>
  values && values.length > 0 ? values.join(',') : undefined

/* "<codigo> - <nombre completo>", omitiendo el codigo si el backend no lo manda */
const buildEmployeeLabel = (row: ShareNotificationResponse): string => {
  const name = (row.employeeName ?? '').replace(/\s+/g, ' ').trim()
  const code = (row.employeeCode ?? '').trim()
  if (!code) return name || '—'
  return `${code} - ${name}`
}

const toNotification = (row: ShareNotificationResponse): ShareNotification => ({
  id: row.shareId,
  employeeId: row.employeeId,
  employeeLabel: buildEmployeeLabel(row),
  recipientEmail: row.recipientEmail ?? '—',
  sentAt: formatExportDate(row.sentAt ?? undefined),
  openCount: Number(row.openCount) || 0,
  lastOpenedAt: formatExportDate(row.lastOpenedAt ?? undefined),
  approvedAt: formatExportDate(row.approvedAt ?? undefined),
  error: row.error ?? ''
})

/* Pagina vacia, para cuando no hay nomina seleccionada */
const emptyPage = (pageSize: number): ShareNotificationsPage => ({
  items: [],
  totalItems: 0,
  totalPages: 0,
  page: 1,
  pageSize
})

/**
 * Pagina de notificaciones (shares) de los filtros seleccionados en la pantalla.
 * GET documents/document-shares?closedPayrollIds=&employeeIds=&page=&pageSize=
 *
 * El backend pagina en SQL (LIMIT/OFFSET + COUNT), asi que los totales son del
 * resultado completo del filtro y no solo de la pagina devuelta.
 */
export async function getShareNotifications(
  filters: ShareNotificationFilters,
  page: number,
  pageSize: number
): Promise<ShareNotificationsPage> {
  if (filters.closedPayrollIds.length === 0) return emptyPage(pageSize)

  const response = await httpPayroll.get<ShareNotificationsResponse>(
    SHARES_PATH,
    {
      params: {
        closedPayrollIds: toCsv(filters.closedPayrollIds),
        employeeIds: toCsv(filters.employeeIds),
        search: filters.search?.trim() || undefined,
        page,
        pageSize
      }
    }
  )

  const data = response?.data

  /* `items` es el listado paginado; `rows` es el backend anterior, que
   * devolvia todas las filas de una vez y sin totales */
  if (Array.isArray(data?.items)) {
    return {
      items: data.items.map(toNotification),
      totalItems: data.totalItems ?? data.items.length,
      totalPages: data.totalPages ?? 1,
      page: data.page ?? page,
      pageSize: data.pageSize ?? pageSize
    }
  }

  const rows = Array.isArray(data?.rows) ? data.rows : []

  return {
    items: rows.map(toNotification),
    totalItems: rows.length,
    totalPages: rows.length > 0 ? 1 : 0,
    page: 1,
    pageSize
  }
}

/* Prefijo del Excel de notificaciones */
const NOTIFICATIONS_FILE_PREFIX = 'comprobantes_compartidos_nomina'

/* Marcas diacriticas que NFD separa de su letra base */
const DIACRITICS = /[̀-ͯ]/g

/* Sin acentos, minusculas y con _ en vez de separadores, para usarlo como nombre de archivo */
const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

/* comprobantes_compartidos_nomina_<codigo>_<fin de periodo>.xlsx */
const notificationsFileName = (
  payrollCode: string,
  periodEndDate: string
): string => {
  const parts = [
    NOTIFICATIONS_FILE_PREFIX,
    slugify(payrollCode),
    periodEndDate
  ].filter(Boolean)
  return `${parts.join('_')}.xlsx`
}

/**
 * Descarga el Excel de la nomina y empleados indicados.
 * GET documents/document-shares?format=excel (responde el xlsx como binario).
 *
 * Deliberadamente NO recibe la busqueda de la tabla: el Excel exporta todo lo
 * seleccionado en el filtro superior, independiente de lo que muestre la tabla.
 */
export async function downloadShareNotificationsExcel(
  filters: ShareNotificationFilters,
  payrollCode: string,
  periodEndDate: string
): Promise<void> {
  const response = await httpPayroll.get<Blob>(SHARES_PATH, {
    params: {
      closedPayrollIds: toCsv(filters.closedPayrollIds),
      employeeIds: toCsv(filters.employeeIds),
      format: 'excel'
    },
    responseType: 'blob'
  })

  if (!response?.data) return

  /* El xlsx llega como binario: se materializa un enlace temporal para bajarlo */
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = notificationsFileName(payrollCode, periodEndDate)
  link.click()
  window.URL.revokeObjectURL(url)
}
